<template>
  <div class="chat-history-list">
    <v-container class="list-container" data-is-scrollable>
      <v-row
        v-for="group in groupedChatHistory"
        :key="group.month"
        v-show="group.entries.length > 0"
      >
        <v-col cols="12">
          <v-card class="chat-group" light flat>
            <v-card-title class="chat-month text-h6">{{
              formatMonth(group.month)
            }}</v-card-title>
            <v-list class="chat-list" light flat>
              <template v-for="entry in group.entries">
                <v-hover v-slot="{ hover }" :key="`hover_${entry.id}`">
                  <v-list-item
                    class="pl-1 pr-0 justify-between"
                    link
                    :key="entry.id"
                  >
                    <v-list-item-content
                      class="pa-0 mr-2"
                      v-if="isEdit && entry.id === selectedEntry"
                    >
                      <v-text-field
                        color="block"
                        light
                        v-model="currentTitle"
                        dense
                        outlined
                        hide-details
                        @click.stop
                        @keydown="
                          (e) =>
                            e.key === 'Enter' ? editedUpdate(entry) : null
                        "
                        class="subtitle-2"
                      ></v-text-field>
                    </v-list-item-content>
                    <v-list-item-content
                      class="mr-2 py-1"
                      v-else
                      @click="handleSelectHistory(entry)"
                    >
                      <v-list-item-title
                        :title="entry.title"
                        class="pl-2 subtitle-2 entry-title"
                        >{{ entry.title }}</v-list-item-title
                      >
                    </v-list-item-content>
                    <v-list-item-action
                      class="d-flex ma-0 flex-row align-center justify-self-end"
                    >
                      <template v-if="isEdit && entry.id === selectedEntry">
                        <v-btn
                          rounded
                          height="25"
                          width="25"
                          light
                          x-small
                          class="mr-2"
                          :disabled="titleDisabled"
                          @click="editedUpdate(entry)"
                        >
                          <v-icon x-small class="px-1"> mdi-check </v-icon>
                        </v-btn>
                        <v-btn
                          rounded
                          height="25"
                          width="25"
                          light
                          x-small
                          @click="editedCancel(entry)"
                        >
                          <v-icon x-small class="px-1"> mdi-close </v-icon>
                        </v-btn>
                      </template>
                      <template v-else>
                        <v-btn
                          rounded
                          height="25"
                          width="25"
                          light
                          x-small
                          :class="{ 'hide-btns': !hover }"
                          @click="handleDeleteHistory(entry)"
                          class="mr-2"
                        >
                          <v-icon x-small class="px-1"> mdi-trash-can </v-icon>
                        </v-btn>
                        <v-btn
                          rounded
                          height="25"
                          width="25"
                          light
                          x-small
                          :class="{ 'hide-btns': !hover }"
                          @click="handleEditHistory(entry)"
                        >
                          <v-icon x-small class="px-1"> mdi-pencil </v-icon>
                        </v-btn>
                      </template>
                    </v-list-item-action>
                  </v-list-item>
                </v-hover>
              </template>
            </v-list>
            <v-divider></v-divider>
          </v-card>
        </v-col>
      </v-row>
      <div ref="observerTarget"></div>
      <v-row v-if="showSpinner" justify="center" align="center">
        <v-col cols="12" class="spinner-container text-center">
          <v-progress-circular
            indeterminate
            size="24"
            aria-label="loading more chat history"
            class="spinner"
          ></v-progress-circular>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { mapActions } from 'vuex'
export default defineComponent({
  name: 'ChatHistoryList',
  components: {},
  props: {
    groupedChatHistory: {
      type: Array,
      default: () => [] as any[],
      required: true,
    },
    showSpinner: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedEntry: null,
      isEdit: false,
      currentTitle: '',
    }
  },
  computed: {
    titleDisabled() {
      return this.currentTitle.trim().length === 0
    },
  },
  methods: {
    ...mapActions('Chat', ['updateCurrentChat']),
    handleSelectHistory(entry: any) {
      this.updateCurrentChat(entry)
      this.$emit('selectHistory', entry)
    },
    handleDeleteHistory(entry: any) {
      this.isEdit = false
      this.selectedEntry = entry.id
      this.$emit('deleteHistory', entry)
    },
    onEdit(entry: any) {
      this.selectedEntry = entry.id
      this.isEdit = true
    },
    editedCancel() {
      this.selectedEntry = null
      this.isEdit = false
    },
    editedUpdate() {
      if (this.titleDisabled) {
        return
      }
      this.isEdit = false
      this.$emit('editHistory', {
        id: this.selectedEntry,
        title: this.currentTitle,
      })
      this.selectedEntry = null
    },
    handleEditHistory(entry: any) {
      this.currentTitle = entry.title
      this.selectedEntry = entry.id
      this.isEdit = true
    },
    formatMonth(month: string) {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()

      const [monthName, yearString] = month.split(' ')
      const year = parseInt(yearString)

      if (year === currentYear) {
        return monthName
      } else {
        return month
      }
    },
    loadMoreEntries() {
      this.$emit('loadMoreHistory')
    },
  },
  mounted() {
    const observerTarget = this.$refs.observerTarget
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 1.0,
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadMoreEntries()
        }
      })
    }, options)

    if (observerTarget) {
      observer.observe(observerTarget)
    }
  },
})
</script>

<style lang="scss">
.chat-history-list {
  overflow: hidden auto;
  max-width: calc(100% - 10px);
  max-height: calc(80vh - 120px);
  .hide-btns {
    visibility: hidden;
  }
  .v-input__control > .v-input__slot {
    min-height: 28px !important;
    padding-left: 9px !important;
  }
  .entry-title {
    border: 1px solid transparent;
  }
  .v-list-item {
    min-height: 32px;
  }
}
</style>
